import React, { memo, useCallback, useEffect, useRef } from "react";
import _noop from "lodash/noop";
import styled from "@emotion/styled";
import JsBarcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";

import { DELIVERY_TYPE_NORMAL } from "@/Models/Delivery";

const PrintContainer = styled.div``;
const WaybillContainer = styled.div``;

/**
 * Style
 */
const PrintContainerStyle = {
  padding: 0,
  margin: 0,
};

const WaybillContainerStyle = {
  position: "relative",
  width: "100mm",
  height: "6in",
  margin: 0,
  padding: 0,
};

const DefaultStyle = {
  transform: "rotate(-90deg)",
  transformOrigin: "left top",
  position: "absolute",
  fontSize: `11px`,
  whiteSpace: "pre-wrap",
  lineHeight: "3mm",
};

const Barcode = ({ bookId, onLoaded = _noop }) => {
  useEffect(() => {
    onLoaded();
  });

  return (
    <DefaultComponent
      style={{
        top: "148mm",
        left: "79mm",
        width: "60mm",
        height: "12mm",
      }}
    >
      <JsBarcode
        margin={0}
        width={1.7}
        height={40}
        value={bookId}
        textPosition="top"
      />
    </DefaultComponent>
  );
};

const BarcodeForSpecificCorpUser = ({ code, title = "" }) => {
  return (
    <DefaultComponent
      style={{
        top: "53mm",
        left: "25mm",
        fontSize: "10px",
        whiteSpace: "pre-wrap",
        lineHeight: "3mm",
      }}
    >
      <div
        style={{
          top: "35mm",
          left: "22mm",
          fontSize: "10px",
          whiteSpace: "pre-wrap",
          lineHeight: "3mm",
          textAlign: "center",
        }}
      >
        <p
          style={{
            margin: 0,
            padding: 0,
          }}
        >
          {title}
        </p>
        <JsBarcode
          fontSize="10"
          margin={0}
          width={1.3}
          height={37}
          value={code}
        />
      </div>
    </DefaultComponent>
  );
};

const BookIdQRCode = ({ bookId }) => {
  return (
    <DefaultComponent
      style={{
        top: "87mm",
        left: "83mm",
        width: "15mm",
        height: "15mm",
      }}
    >
      <QRCodeSVG
        value={bookId}
        style={{ width: "15mm", height: "15mm", display: "block" }}
      />
    </DefaultComponent>
  );
};

const DefaultComponent = ({ children, style = {} }) => {
  return <div style={{ ...DefaultStyle, ...style }}>{children}</div>;
};

const DongCode = ({ dongCode }) => {
  return (
    <DefaultComponent
      style={{
        top: `${
          15 +
          8 * dongCode.returnPickup.length +
          4 * dongCode.sidodong.length +
          8 * dongCode.allocationGroup.length
        }mm`,
        left: "17mm",
        textAlign: "right",
        letterSpacing: "0.1mm",
      }}
    >
      <span
        style={{
          fontSize: dongCode.returnPickup ? "40px" : "20px",
          fontWeight: "bold",
          whiteSpace: "pre",
        }}
      >
        {dongCode.returnPickup || dongCode.sidodong}
      </span>
      <span
        style={{
          fontSize: "40px",
          fontWeight: "bold",
          whiteSpace: "pre",
        }}
      >
        {dongCode.allocationGroup}
      </span>
    </DefaultComponent>
  );
};

const Etc = memo(({ orderIdFromCorp, logoImage, onAfterRendered = _noop }) => {
  useEffect(() => {
    if (!logoImage) {
      setTimeout(() => {
        onAfterRendered();
      }, 200);
    }
  }, []);

  const etcStyle = {
    top: "87mm",
    left: "5mm",
    fontSize: "12px",
    whiteSpace: "pre",
  };

  if (orderIdFromCorp) {
    return (
      <DefaultComponent style={etcStyle}>
        {orderIdFromCorp.length > 20
          ? `${orderIdFromCorp.slice(0, 20)}\n${orderIdFromCorp.slice(20)}`
          : orderIdFromCorp}
      </DefaultComponent>
    );
  } else if (logoImage) {
    return (
      <DefaultComponent style={etcStyle}>
        <img
          onLoad={onAfterRendered}
          alt="기업 로고 이미지"
          src={logoImage.src}
          style={{
            width: logoImage.width,
            height: logoImage.height,
          }}
        />
      </DefaultComponent>
    );
  } else {
    return null;
  }
});

const Memo = ({ children }) => {
  return (
    <DefaultComponent
      style={{
        top: "145mm",
        left: "65mm",
        width: "55mm",
        fontSize: "14px",
        fontWeight: "bold",
        lineHeight: "3.5mm",
      }}
    >
      {children}
    </DefaultComponent>
  );
};

const OrderIdFromCorpWithSpotInfo = ({ orderIdFromCorp, spot }) => {
  return (
    <DefaultComponent
      style={{
        top: "82mm",
        left: "23mm",
        fontSize: "10px",
        whiteSpace: "pre-wrap",
        lineHeight: "3mm",
      }}
    >
      <div style={{ marginTop: "3px", marginRight: "3px" }}>
        {orderIdFromCorp && <span>주문번호 {orderIdFromCorp} </span>}
        {spot && (
          <span>
            ({spot.name}/T.{spot.contactForLabel || ""})
          </span>
        )}
      </div>
    </DefaultComponent>
  );
};

const ProductNames = ({ productNames = [], delivery = {} }) => {
  const customProductName =
    delivery.isSpotCovering ||
    delivery.isSpotUgleelab ||
    delivery.isSpotDduckhamji;

  return (
    <DefaultComponent
      style={{
        top: "85mm",
        left: "39mm",
        lineHeight:
          delivery.isSpotKukka || delivery.isSpotDialCommunication
            ? "4.2mm"
            : "3.5mm",
        fontSize:
          delivery.isSpotKukka || delivery.isSpotDialCommunication
            ? "17px"
            : "12px",
        fontWeight: "bold",
        width: "80mm",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
      }}
    >
      <div>
        {!customProductName &&
          productNames?.map((name, index) => {
            return (
              <div key={`${name}-${index}`}>
                {Array.isArray(name) ? name.join("") : name}{" "}
              </div>
            );
          })}
        {delivery.isSpotCovering &&
          productNames?.map((name, index) => {
            const fullName = Array.isArray(name) ? name.join("") : name;
            const [firstWord, ...rest] = fullName.split(" ");

            return (
              <div
                key={`${name}-${index}`}
                style={{
                  fontSize: "30px",
                  lineHeight: "8mm",
                  marginTop: "4px",
                }}
              >
                *{firstWord.slice(1)}* {rest.join(" ")}{" "}
              </div>
            );
          })}
        {delivery.isSpotUgleelab &&
          productNames?.map((name, index) => {
            const fullName = Array.isArray(name) ? name.join("") : name;
            const [firstWord, ...rest] = fullName.split(" ");
            const displayedFirstWord = firstWord.slice(1);
            const isTodayPickup = displayedFirstWord === "오늘수거";

            return (
              <div
                key={`${name}-${index}`}
                style={{
                  fontSize: "30px",
                  lineHeight: "8mm",
                  marginTop: "4px",
                }}
              >
                {displayedFirstWord}
                {isTodayPickup ? <br /> : " "}
                {rest.join(" ")}
              </div>
            );
          })}
        {delivery.isSpotDduckhamji && (
          <div
            style={{
              fontSize: "20px",
              lineHeight: "6mm",
              marginTop: "4px",
            }}
          >
            {productNames?.map((name, index) => {
              return (
                <div key={`${name}-${index}`}>
                  {Array.isArray(name) ? name.join("") : name}{" "}
                </div>
              );
            })}
            <div style={{ fontSize: "18px" }}>
              떡입니다빠른배송
              <br />
              보내는분:{delivery.displaySenderName}
              {delivery.displaySenderMobile}
            </div>
          </div>
        )}
      </div>
    </DefaultComponent>
  );
};

const ProductNamesRest = ({ productNames = [] }) => {
  return (
    <DefaultComponent
      style={{
        top: "85mm",
        left: "15mm",
        lineHeight: "3.5mm",
        fontSize: "12px",
        fontWeight: "bold",
        whiteSpace: "pre-wrap",
      }}
    >
      {productNames.join("\n")}
    </DefaultComponent>
  );
};

const ProductTotalCount = ({ totalCount, numbering }) => {
  return (
    <DefaultComponent
      style={{
        top: "82mm",
        left: "30mm",
        fontSize: "10px",
        whiteSpace: "pre-wrap",
      }}
    >
      <div>{totalCount}</div>
      <div>{numbering}</div>
    </DefaultComponent>
  );
};

const ReceiptDate = ({ children }) => {
  return (
    <DefaultComponent
      style={{
        top: "82mm",
        left: "27mm",
        fontSize: "10px",
        whiteSpace: "pre-wrap",
        lineHeight: "3mm",
      }}
    >
      {children}
    </DefaultComponent>
  );
};

const ReceiverAddress = ({
  receiverAddressWithoutSigungu,
  receiverAddressRoadWithoutSigungu,
  receiverBuilding,
}) => {
  const _text = `${`${receiverAddressRoadWithoutSigungu} ${receiverBuilding}`.trim()}/${receiverAddressWithoutSigungu}`;

  const text1 = _text.slice(0, 15);
  const text2 = _text.slice(15, 30);
  const text3 = _text.slice(30);

  return (
    <>
      <DefaultComponent
        style={{
          top: "145mm",
          left: "27mm",
          fontSize: "17px",
          fontWeight: "bold",
          lineHeight: "4.1mm",
        }}
      >
        {text1}
      </DefaultComponent>
      <DefaultComponent
        style={{
          top: "145mm",
          left: "32mm",
          fontSize: "17px",
          fontWeight: "bold",
          lineHeight: "4.1mm",
        }}
      >
        {text2}
      </DefaultComponent>
      <DefaultComponent
        style={{
          top: "145mm",
          left: "37mm",
          fontSize: "17px",
          fontWeight: "bold",
          lineHeight: "4.1mm",
        }}
      >
        {text3}
      </DefaultComponent>
    </>
  );
};

const ReceiverNameAndMobile = ({ receiverName, receiverMobile }) => {
  return (
    <DefaultComponent
      style={{
        top: "145mm",
        left: "57mm",
        width: "400px",
      }}
    >
      <div
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          lineHeight: "3mm",
        }}
      >
        {`<${receiverName}>`}
      </div>
      <div
        style={{
          fontSize: "16px",
          marginTop: "4px",
          fontWeight: "bold",
          lineHeight: "3mm",
        }}
      >
        T: {`${receiverMobile}`.trim()}
      </div>
    </DefaultComponent>
  );
};

const RestProductComponets = ({
  numOfLinesRestProductNames,
  restProductNames,
}) => {
  let productNamesComponents = [];
  for (
    let i = 0;
    i < restProductNames.length;
    i += numOfLinesRestProductNames
  ) {
    productNamesComponents.push(
      <WaybillContainer key={i} className="label" style={WaybillContainerStyle}>
        <ProductNamesRest
          productNames={restProductNames.slice(
            i,
            i + numOfLinesRestProductNames,
          )}
        />
        <PrintBreak />
      </WaybillContainer>,
    );
  }

  return productNamesComponents;
};

const SenderName = ({ children }) => {
  return (
    <DefaultComponent
      style={{
        top: "145mm",
        left: "15mm",
        fontSize: "16px",
        fontWeight: "bold",
        width: "100mm",
      }}
    >
      {children}
    </DefaultComponent>
  );
};

const SenderAddress = ({ children }) => {
  return (
    <DefaultComponent
      style={{
        top: "145mm",
        left: "19mm",
        fontSize: "11px",
        width: "65mm",
      }}
    >
      {children}
    </DefaultComponent>
  );
};

const PrintBreak = () => {
  return <div sytle={{ pageBreakAfter: "alwayws" }}></div>;
};

const getCharWidth = (char) => {
  if (/[가-힣]/.test(char)) return 1; // 한글
  if (/[A-Z]/.test(char)) return 0.6; // 영문 대문자
  if (/[a-z]/.test(char)) return 0.5; // 영문 소문자
  if (/[0-9]/.test(char)) return 0.5; // 숫자
  if (/\s/.test(char)) return 0.5; // 공백
  if (/[.,\-/]/.test(char)) return 0.3; // 특수문자 (주소에 자주 쓰이는)
  return 0.5; // 그 외
};

const splitByVisualWidth = (text, maxWidth = 15) => {
  const lines = [];
  let currentLine = "";
  let currentWidth = 0;

  for (const char of text) {
    const charWidth = getCharWidth(char);

    if (currentWidth + charWidth > maxWidth) {
      lines.push(currentLine);
      currentLine = char;
      currentWidth = charWidth;
    } else {
      currentLine += char;
      currentWidth += charWidth;
    }
  }

  if (currentLine) lines.push(currentLine);

  return [lines[0] || "", lines[1] || "", lines[2] || ""];
};

const UnrefinedAddress = ({ unrefinedAddress }) => {
  const _text = unrefinedAddress ? unrefinedAddress.trim() : "";
  const [text1, text2, text3] = splitByVisualWidth(_text, 15);

  return (
    <>
      <DefaultComponent
        style={{
          top: "145mm",
          left: "42mm",
          width: "55mm",
          fontSize: "14px",
          fontWeight: "bold",
          lineHeight: "4mm",
        }}
      >
        {text1}
      </DefaultComponent>
      <DefaultComponent
        style={{
          top: "145mm",
          left: "45.5mm",
          width: "55mm",
          fontSize: "14px",
          fontWeight: "bold",
          lineHeight: "4mm",
        }}
      >
        {text2}
      </DefaultComponent>
      <DefaultComponent
        style={{
          top: "145mm",
          left: "49mm",
          width: "55mm",
          fontSize: "14px",
          fontWeight: "bold",
          lineHeight: "4mm",
        }}
      >
        {text3}
      </DefaultComponent>
    </>
  );
};

const WaybillWrap = memo(
  ({ delivery, count, totalCount, handleAfterRendered = _noop }) => {
    return (
      <>
        <WaybillContainer className="label" style={WaybillContainerStyle}>
          {/** 운송장 상단 영역 */}
          <Etc
            logoImage={delivery.displayCorpLogoImageObjBySpotCode}
            orderIdFromCorp={delivery.displayOrderIdFromCorpBySpotCode}
            onAfterRendered={handleAfterRendered}
          />

          {/** 운송장 왼쪽 영역 */}
          {delivery.type === DELIVERY_TYPE_NORMAL ? (
            <>
              <SenderName>
                {delivery.displaySenderName} T. {delivery.displaySenderMobile}
              </SenderName>
              <SenderAddress>
                {delivery.displaySenderAddress}
                <br />
                {delivery.displaySenderAddressDetail}
              </SenderAddress>
              <ReceiverAddress
                receiverAddressWithoutSigungu={
                  delivery.displayReceiverAddressWithoutSigungu
                }
                receiverAddressRoadWithoutSigungu={
                  delivery.displayReceiverAddressRoadWithoutSigungu
                }
                receiverBuilding={delivery.displayReceiverBuilding || ""}
              />
              <UnrefinedAddress
                unrefinedAddress={delivery.displayUnrefinedAddress}
              />
              <ReceiverNameAndMobile
                receiverName={delivery.displayReceiverName}
                receiverMobile={delivery.displayReceiverMobile}
              />
            </>
          ) : (
            <>
              <SenderName>
                {delivery.displayReceiverName} T.{" "}
                {delivery.displayReceiverMobile}
              </SenderName>
              <SenderAddress>
                {delivery.receiverAddress}
                <br />
                {delivery.displayReceiverAddressDetail}
              </SenderAddress>
              <ReceiverAddress
                receiverAddressWithoutSigungu={
                  delivery.senderAddressWithoutSigungu
                }
                receiverAddressRoadWithoutSigungu={
                  delivery.senderAddressRoadWithoutSigungu
                }
                receiverBuilding={delivery.senderBuilding || ""}
              />
              <UnrefinedAddress
                unrefinedAddress={delivery.displayUnrefinedAddress}
              />
              <ReceiverNameAndMobile
                receiverName={delivery.displaySenderName}
                receiverMobile={delivery.displaySenderMobile}
              />
            </>
          )}

          <Memo>{delivery.memoFromCustomer}</Memo>
          <Barcode bookId={delivery.bookId} />

          {/** 운송장 오른쪽 영역 */}
          <DongCode dongCode={delivery.dongCode} />
          <OrderIdFromCorpWithSpotInfo
            orderIdFromCorp={delivery.orderIdFromCorp}
            spot={delivery.spot}
          />

          <ProductNames
            productNames={delivery.firstProductNames}
            delivery={delivery}
          />

          <BookIdQRCode bookId={delivery.bookId} />

          <ProductTotalCount
            totalCount={delivery.renderTotalProductCount()}
            numbering={delivery.renderNumbering(count, totalCount)}
          />

          <ReceiptDate>
            {delivery.renderReceiptDate("yyyy/MM/dd HH:mm")}
          </ReceiptDate>

          {delivery.barcodeCodeBySpotCode && (
            <BarcodeForSpecificCorpUser
              code={delivery.barcodeCodeBySpotCode}
              title={delivery.barcodeTitleBySpotCode || null}
            />
          )}

          <PrintBreak />
        </WaybillContainer>

        <RestProductComponets
          numOfLinesRestProductNames={delivery.numOfLinesRestProductNames}
          restProductNames={delivery.restProductNames}
        />
      </>
    );
  },
);

export default function Print100150V3Component({
  deliveries = [],
  onLoaded = _noop,
}) {
  const loadedCount = useRef(0);

  const handleAfterRendered = useCallback(() => {
    loadedCount.current++;
    if (loadedCount.current === deliveries.length) {
      onLoaded();
    }
  }, [loadedCount.current]);

  return (
    <PrintContainer style={PrintContainerStyle}>
      {deliveries.map((d, index) => {
        return (
          <WaybillWrap
            key={index}
            delivery={d}
            count={index + 1}
            totalCount={deliveries.length}
            handleAfterRendered={handleAfterRendered}
          />
        );
      })}
    </PrintContainer>
  );
}
